// src/components/helpdesk/PhoneAICommandCenter.js
// --- UPDATE (Phone AI Helpdesk) 24-hour phone AI command center for the Helpdesk page:
// live calls with streaming transcripts, every public report with its stage, the reports map
// (hot spots + drains near flooding), clusters, AI/ML learning status, and manual phone intake.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, HStack, VStack, Text, Badge, Button, SimpleGrid, Tabs, TabList, Tab, TabPanels, TabPanel, Table, Thead, Tbody, Tr, Th, Td,
  Select, Input, Textarea, Spinner, useToast, useColorModeValue, Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody,
  DrawerCloseButton, useDisclosure, Icon, Tooltip, Alert, AlertIcon, Wrap, WrapItem, Stat, StatLabel, StatNumber, StatHelpText, Code,
} from '@chakra-ui/react';
import { MdPhoneInTalk, MdMap, MdListAlt, MdHub, MdPsychology, MdAddCall, MdRefresh, MdCircle } from 'react-icons/md';
import webSocketService from '../../services/websocketService';
import phoneHelpdeskApi, { CATEGORY_META, STAGE_LABEL, SEVERITY_COLOR, wsPayload } from '../../services/phoneHelpdeskApi';
import PublicReportsMap from './PublicReportsMap';
import ReportTracePanel from './ReportTracePanel';

const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
const n = (v) => (v === null || v === undefined ? '—' : v);

function StatTile({ label, value, help, color }) {
  const bg = useColorModeValue('white', 'gray.800');
  return (
    <Stat p={3} bg={bg} borderRadius="lg" borderWidth="1px" borderColor={useColorModeValue('gray.200', 'whiteAlpha.200')}>
      <StatLabel fontSize="xs" color="gray.500">{label}</StatLabel>
      <StatNumber fontSize="xl" color={color}>{n(value)}</StatNumber>
      {help && <StatHelpText fontSize="10px" mb={0}>{help}</StatHelpText>}
    </Stat>
  );
}

export default function PhoneAICommandCenter({ onOpenTicket }) {
  const toast = useToast();
  const drawer = useDisclosure();
  const [stats, setStats] = useState(null);
  const [calls, setCalls] = useState([]);
  const [activeCall, setActiveCall] = useState(null);
  const [turns, setTurns] = useState([]);
  const [reports, setReports] = useState({ rows: [], total: 0 });
  const [filters, setFilters] = useState({ group: '', open: '1', verification: '', page: 1 });
  const [clusters, setClusters] = useState([]);
  const [ai, setAi] = useState(null);
  const [traceId, setTraceId] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wsStatus, setWsStatus] = useState('idle');
  const [intake, setIntake] = useState({ description: '', location_text: '', caller_phone: '', caller_name: '', category: '' });
  const [submitting, setSubmitting] = useState(false);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [ask, setAsk] = useState('');
  const [answer, setAnswer] = useState('');
  const [streaming, setStreaming] = useState(false);
  const streamRef = useRef(null);
  const activeCallRef = useRef(null);
  const refreshTimer = useRef(null);
  const panelBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'whiteAlpha.200');
  const bubbleBg = useColorModeValue('gray.100', 'whiteAlpha.100');

  const loadStats = useCallback(() => phoneHelpdeskApi.stats(30).then(setStats).catch(() => {}), []);
  const loadCalls = useCallback(() => phoneHelpdeskApi.calls({ days: 2 }).then((r) => setCalls(r || [])).catch(() => {}), []);
  const loadReports = useCallback(() => {
    const p = { limit: 25, page: filters.page };
    if (filters.group) p.group = filters.group;
    if (filters.open) p.open = filters.open;
    if (filters.verification) p.verification = filters.verification;
    return phoneHelpdeskApi.reports(p).then((r) => setReports(r || { rows: [], total: 0 })).catch(() => {});
  }, [filters]);
  const loadClusters = useCallback(() => phoneHelpdeskApi.clusters({ days: 30 }).then((r) => setClusters(r || [])).catch(() => {}), []);
  const loadAi = useCallback(() => phoneHelpdeskApi.aiStatus().then(setAi).catch(() => {}), []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadStats(), loadCalls(), loadReports(), loadClusters()]);
    setMapKey((k) => k + 1);
    setLoading(false);
  }, [loadStats, loadCalls, loadReports, loadClusters]);

  useEffect(() => { refreshAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);

  // live events (ws envelope {type:'helpdesk', payload:{event,...}})
  useEffect(() => {
    const debounced = () => {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => { loadStats(); loadReports(); loadCalls(); loadClusters(); setMapKey((k) => k + 1); }, 800);
    };
    const onMsg = (raw) => {
      const m = wsPayload(raw);
      if (!m || !m.event) return;
      if (m.event === 'call_turn') {
        if (activeCallRef.current && activeCallRef.current === m.call_id) setTurns((t) => [...t, { seq: `live-${Date.now()}-${t.length}`, role: m.role, text: m.text, latency_ms: m.latency_ms }]);
        return;
      }
      if (m.event === 'call_started' || m.event === 'call_ended') { loadCalls(); loadStats(); return; }
      if (m.event === 'phone_report_created') {
        toast({ title: `New ${m.channel || 'phone'} report: ${m.label || m.category}`, description: `${m.ticket_number} · ${m.severity}`, status: m.severity === 'critical' ? 'warning' : 'info', position: 'top-right', duration: 4000, isClosable: true });
      }
      if (['phone_report_created', 'phone_report_geotagged', 'report_verified', 'ticket_resolved', 'work_order_linked', 'inspection_scheduled', 'cluster_updated', 'ai_retrained'].includes(m.event)) debounced();
    };
    // websocketService dispatches on the frame's `type` and returns an unsubscribe function.
    // connect() is idempotent (NotificationContext usually opened the socket already).
    webSocketService.connect();
    const offs = [
      webSocketService.subscribe('helpdesk', onMsg),
      // crews changing a work order's status move phone reports along (in repair / resolved)
      webSocketService.subscribe('work_order_events', (raw) => { const p = wsPayload(raw); if (p && ['status_updated', 'assigned', 'created'].includes(p.type)) debounced(); }),
      // after a reconnect, reload anything missed while the socket was down
      webSocketService.subscribe('status', (st) => {
        setWsStatus(st?.status || 'idle');
        if (st?.status === 'connected') debounced();
      }),
    ];
    setWsStatus(webSocketService.getStatus?.().status || 'idle');
    return () => { offs.forEach((off) => { try { off(); } catch (_) { /* already removed */ } }); clearTimeout(refreshTimer.current); };
  }, [loadCalls, loadReports, loadStats, loadClusters, toast]);

  const openCall = async (c) => {
    setActiveCall(c.id);
    setTurns([]);
    try { const d = await phoneHelpdeskApi.call(c.id); setTurns(d.turns || []); } catch (_) { setTurns([]); }
  };
  const openTrace = useCallback((reportId) => { setTraceId(reportId); drawer.onOpen(); }, [drawer]);

  const submitIntake = async () => {
    setSubmitting(true);
    try {
      const r = await phoneHelpdeskApi.createReport({ ...intake, category: intake.category || undefined, channel: 'phone' });
      toast({ title: r.duplicate ? 'Added to existing report' : 'Report logged', description: `${r.ticket_number || ''} · ${r.label}`, status: 'success', duration: 4000, isClosable: true });
      setIntake({ description: '', location_text: '', caller_phone: '', caller_name: '', category: '' });
      refreshAll();
      if (r.report_id) openTrace(r.report_id);
    } catch (e) {
      toast({ title: 'Could not log report', description: e.response?.data?.message || e.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  const runAsk = () => {
    if (streamRef.current) streamRef.current.abort();
    setAnswer('');
    setStreaming(true);
    streamRef.current = phoneHelpdeskApi.assistStream({ prompt: ask, mode: 'general' }, {
      onToken: (_t, full) => setAnswer(full),
      onDone: () => setStreaming(false),
      onError: (e) => { setStreaming(false); setAnswer(`AI unavailable: ${e.message}`); },
    });
  };
  useEffect(() => () => streamRef.current && streamRef.current.abort(), []);

  const s = stats || {};
  const live = calls.filter((c) => c.status === 'in_progress');

  return (
    <Box bg={panelBg} borderRadius="2xl" borderWidth="1px" borderColor={border} p={4} mb={6}>
      <HStack justify="space-between" mb={3} flexWrap="wrap">
        <HStack><Icon as={MdPhoneInTalk} boxSize={6} color="green.500" /><Text fontSize="lg" fontWeight="800">24-hour Phone AI Command Center</Text>
          {live.length > 0 && <Badge colorScheme="green" variant="solid"><HStack spacing={1}><Icon as={MdCircle} boxSize={2} /><Text>{live.length} live</Text></HStack></Badge>}
          <Tooltip label={wsStatus === 'connected' ? 'Live updates on' : `Live updates ${wsStatus} — lists still refresh when you press Refresh`}>
            <Badge variant="subtle" colorScheme={wsStatus === 'connected' ? 'green' : wsStatus === 'reconnecting' || wsStatus === 'connecting' ? 'yellow' : 'gray'}>{wsStatus === 'connected' ? 'LIVE' : wsStatus}</Badge>
          </Tooltip></HStack>
        <Button size="sm" leftIcon={<MdRefresh />} onClick={refreshAll} isLoading={loading}>Refresh</Button>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 4, xl: 8 }} spacing={3} mb={4}>
        <StatTile label="Reports (30 d)" value={s.reports?.total} />
        <StatTile label="Open" value={s.reports?.open} color="orange.400" />
        <StatTile label="Resolved" value={s.reports?.resolved} color="green.400" />
        <StatTile label="Hazards" value={s.reports?.hazards} color="red.400" />
        <StatTile label="Hot spots" value={s.clusters?.hot_spots} help={`${n(s.clusters?.active)} active clusters`} />
        <StatTile label="Calls (30 d)" value={s.calls?.total} help={s.calls?.avg_duration_sec != null ? `avg ${s.calls.avg_duration_sec}s` : undefined} />
        <StatTile label="AI response" value={s.calls?.avg_latency_ms != null ? `${s.calls.avg_latency_ms} ms` : null} help="end of speech → reply" />
        <StatTile label="Genuine rate" value={s.verification?.genuine_rate != null ? `${Math.round(s.verification.genuine_rate * 100)} %` : null} help={s.reports?.avg_resolve_hours != null ? `avg fix ${s.reports.avg_resolve_hours} h` : undefined} />
      </SimpleGrid>

      <Tabs variant="soft-rounded" colorScheme="blue" size="sm" isLazy onChange={(i) => { if (i === 4) loadAi(); }}>
        <TabList flexWrap="wrap" gap={1}>
          <Tab><Icon as={MdPhoneInTalk} mr={1} />Calls</Tab>
          <Tab><Icon as={MdListAlt} mr={1} />Public reports</Tab>
          <Tab><Icon as={MdMap} mr={1} />Map</Tab>
          <Tab><Icon as={MdHub} mr={1} />Hot spots</Tab>
          <Tab><Icon as={MdPsychology} mr={1} />AI & learning</Tab>
          <Tab><Icon as={MdAddCall} mr={1} />Log phone report</Tab>
        </TabList>
        <TabPanels>
          {/* calls */}
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
              <Box maxH="420px" overflowY="auto">
                <Table size="sm"><Thead><Tr><Th>Caller</Th><Th>Started</Th><Th>Status</Th><Th>Ticket</Th><Th isNumeric>Latency</Th></Tr></Thead>
                  <Tbody>{calls.length === 0 && <Tr><Td colSpan={5}><Text fontSize="sm" color="gray.500">No calls in the last 2 days.</Text></Td></Tr>}
                    {calls.map((c) => (
                      <Tr key={c.id} cursor="pointer" bg={activeCall === c.id ? bubbleBg : undefined} onClick={() => openCall(c)}>
                        <Td>{c.caller_phone || 'Unknown'}</Td><Td fontSize="xs">{fmt(c.started_at)}</Td>
                        <Td><Badge colorScheme={c.status === 'in_progress' ? 'green' : c.status === 'completed' ? 'gray' : 'orange'}>{c.status === 'in_progress' ? 'LIVE' : c.status}</Badge></Td>
                        <Td>{c.ticket_id ? <Button size="xs" variant="link" onClick={(e) => { e.stopPropagation(); if (c.report_id) openTrace(c.report_id); else if (onOpenTicket) onOpenTicket(c.ticket_id); }}>{c.ticket_number || `#${c.ticket_id}`}</Button> : '—'}</Td>
                        <Td isNumeric>{c.avg_latency_ms != null ? `${c.avg_latency_ms} ms` : '—'}</Td>
                      </Tr>
                    ))}</Tbody></Table>
              </Box>
              <Box borderWidth="1px" borderColor={border} borderRadius="lg" p={3} maxH="420px" overflowY="auto">
                {!activeCall ? <Text fontSize="sm" color="gray.500">Select a call to see its transcript. Live calls update as the caller and AI speak.</Text> : (
                  <VStack align="stretch" spacing={2}>
                    {turns.filter((t) => t.role !== 'tool').map((t) => (
                      <HStack key={t.seq} justify={t.role === 'caller' ? 'flex-start' : 'flex-end'}>
                        <Box maxW="85%" px={3} py={2} borderRadius="lg" bg={t.role === 'caller' ? bubbleBg : 'blue.500'} color={t.role === 'caller' ? undefined : 'white'} fontSize="sm">
                          <Text fontSize="10px" opacity={0.7}>{t.role === 'caller' ? 'Caller' : 'AI'}{t.latency_ms ? ` · ${t.latency_ms} ms` : ''}</Text>{t.text}
                        </Box>
                      </HStack>
                    ))}
                    {turns.filter((t) => t.role === 'tool').length > 0 && <Text fontSize="10px" color="gray.500">{turns.filter((t) => t.role === 'tool').length} system action(s) during the call</Text>}
                  </VStack>
                )}
              </Box>
            </SimpleGrid>
          </TabPanel>

          {/* reports */}
          <TabPanel px={0}>
            <Wrap mb={3} spacing={2}>
              <WrapItem><Select size="sm" w="160px" value={filters.group} onChange={(e) => setFilters({ ...filters, group: e.target.value, page: 1 })}><option value="">All types</option><option value="road">Road reports</option><option value="flood">Flooding / drains</option></Select></WrapItem>
              <WrapItem><Select size="sm" w="150px" value={filters.open} onChange={(e) => setFilters({ ...filters, open: e.target.value, page: 1 })}><option value="1">Open only</option><option value="">All stages</option></Select></WrapItem>
              <WrapItem><Select size="sm" w="170px" value={filters.verification} onChange={(e) => setFilters({ ...filters, verification: e.target.value, page: 1 })}><option value="">Any verification</option><option value="unverified">Unverified</option><option value="confirmed_true">Verified true</option><option value="confirmed_false">Marked false</option><option value="duplicate">Duplicate</option></Select></WrapItem>
              <WrapItem><Text fontSize="sm" color="gray.500" pt={1}>{reports.total} report(s)</Text></WrapItem>
            </Wrap>
            <Box overflowX="auto">
              <Table size="sm"><Thead><Tr><Th>Ticket</Th><Th>Type</Th><Th>Severity</Th><Th>Location</Th><Th>Stage</Th><Th>Callers</Th><Th>Work order</Th><Th>AI</Th><Th>Received</Th></Tr></Thead>
                <Tbody>{(reports.rows || []).map((r) => {
                  const meta = CATEGORY_META[r.category] || { label: r.category, color: 'gray' };
                  return (
                    <Tr key={r.id} cursor="pointer" onClick={() => openTrace(r.id)} _hover={{ bg: bubbleBg }}>
                      <Td fontWeight="600">{r.ticket_number}</Td>
                      <Td><Badge colorScheme={meta.color}>{meta.label}</Badge>{!!r.hazard_to_life && <Badge ml={1} colorScheme="red" variant="solid">!</Badge>}</Td>
                      <Td><Badge colorScheme={SEVERITY_COLOR[r.severity]}>{r.severity}</Badge></Td>
                      <Td maxW="220px"><Text noOfLines={1} fontSize="sm">{r.location_text || '—'}</Text><Text fontSize="10px" color="gray.500">{r.latitude != null ? r.location_source : 'no GPS yet'}</Text></Td>
                      <Td><Badge variant="outline">{STAGE_LABEL[r.lifecycle_stage] || r.lifecycle_stage}</Badge></Td>
                      <Td>{r.cluster_callers || 1}</Td>
                      <Td>{r.work_order_id ? <Badge colorScheme={['completed', 'closed'].includes(r.work_order_status) ? 'green' : 'blue'}>{r.work_order_status || 'linked'}</Badge> : '—'}</Td>
                      <Td><Tooltip label={`Category confidence / likely genuine (${r.ai_engine})`}><Text fontSize="xs">{r.ai_confidence != null ? Math.round(r.ai_confidence * 100) : '—'}% / {r.ai_validity_score != null ? Math.round(r.ai_validity_score * 100) : '—'}%</Text></Tooltip></Td>
                      <Td fontSize="xs">{fmt(r.created_at)}</Td>
                    </Tr>
                  );
                })}</Tbody></Table>
            </Box>
            <HStack justify="flex-end" mt={2}>
              <Button size="xs" isDisabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>Prev</Button>
              <Text fontSize="xs">Page {filters.page}</Text>
              <Button size="xs" isDisabled={filters.page * 25 >= reports.total} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>Next</Button>
            </HStack>
          </TabPanel>

          {/* map */}
          <TabPanel px={0}><PublicReportsMap refreshKey={mapKey} onSelectReport={(p) => openTrace(p.id)} /></TabPanel>

          {/* clusters */}
          <TabPanel px={0}>
            <Box overflowX="auto">
              <Table size="sm"><Thead><Tr><Th>#</Th><Th>Type</Th><Th>Area</Th><Th isNumeric>Callers</Th><Th isNumeric>Reports</Th><Th>Max severity</Th><Th>Work order</Th><Th>Last call</Th></Tr></Thead>
                <Tbody>{clusters.length === 0 && <Tr><Td colSpan={8}><Text fontSize="sm" color="gray.500">No active hot spots.</Text></Td></Tr>}
                  {clusters.map((c) => (
                    <Tr key={c.id}>
                      <Td>{c.id}</Td><Td><Badge colorScheme={c.category_group === 'flood' ? 'blue' : c.category_group === 'road' ? 'orange' : 'purple'}>{c.category_group}</Badge></Td>
                      <Td maxW="260px"><Text noOfLines={1}>{c.label || `${Number(c.center_lat).toFixed(4)}, ${Number(c.center_lng).toFixed(4)}`}</Text></Td>
                      <Td isNumeric fontWeight={c.unique_callers >= 3 ? '800' : 'normal'}>{c.unique_callers}</Td><Td isNumeric>{c.report_count}</Td>
                      <Td><Badge colorScheme={SEVERITY_COLOR[c.max_severity]}>{c.max_severity}</Badge></Td>
                      <Td>{c.work_order_id ? <Badge colorScheme="blue">{c.work_order_status || 'linked'}</Badge> : '—'}</Td>
                      <Td fontSize="xs">{fmt(c.last_seen_at)}</Td>
                    </Tr>
                  ))}</Tbody></Table>
            </Box>
            <Button size="xs" mt={2} variant="outline" onClick={() => phoneHelpdeskApi.recomputeClusters().then(() => { toast({ title: 'Clusters recomputed', status: 'success', duration: 2000 }); loadClusters(); }).catch((e) => toast({ title: 'Recompute failed', description: e.response?.data?.message || e.message, status: 'error' }))}>Recompute clusters (DBSCAN)</Button>
          </TabPanel>

          {/* AI */}
          <TabPanel px={0}>
            {!ai ? <HStack><Spinner size="sm" /><Text fontSize="sm">Loading AI status…</Text></HStack> : (
              <VStack align="stretch" spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <Box p={3} borderWidth="1px" borderColor={border} borderRadius="lg">
                    <Text fontWeight="700" fontSize="sm">Ollama</Text>
                    <Badge colorScheme={ai.ollama?.online ? 'green' : 'red'}>{ai.ollama?.online ? 'online' : 'offline'}</Badge>
                    <Text fontSize="xs" mt={1}>Model <Code fontSize="xs">{ai.ollama?.model}</Code> {ai.ollama?.online && !ai.ollama?.model_pulled ? '(not pulled!)' : ''}</Text>
                  </Box>
                  <Box p={3} borderWidth="1px" borderColor={border} borderRadius="lg">
                    <Text fontWeight="700" fontSize="sm">Learning data</Text>
                    <Text fontSize="xs">{ai.feedback.total} verdicts · {ai.feedback.genuine} true · {ai.feedback.false_reports} false</Text>
                    <Text fontSize="xs">{ai.feedback.pending_for_training} new since last training (auto-retrain every {ai.retrain_every})</Text>
                    <Text fontSize="xs">Live accuracy: {ai.live_accuracy != null ? `${Math.round(ai.live_accuracy * 100)} % on ${ai.live_accuracy_samples}` : '—'}</Text>
                  </Box>
                  <Box p={3} borderWidth="1px" borderColor={border} borderRadius="lg">
                    <Text fontWeight="700" fontSize="sm">Models</Text>
                    {(ai.models || []).length === 0 && <Text fontSize="xs" color="gray.500">Not trained yet — rules + ML seed model in use.</Text>}
                    {(ai.models || []).map((m) => <Text key={`${m.engine}-${m.version}`} fontSize="xs">{m.engine}: {m.version} · {m.samples} samples · acc {m.accuracy != null ? `${Math.round(m.accuracy * 100)} %` : '—'}</Text>)}
                    <Button size="xs" mt={2} colorScheme="purple" onClick={() => phoneHelpdeskApi.retrain().then(() => { toast({ title: 'Retrained', status: 'success', duration: 2500 }); loadAi(); }).catch((e) => toast({ title: 'Retrain failed', description: e.response?.data?.message || e.message, status: 'error' }))}>Retrain now</Button>
                  </Box>
                </SimpleGrid>
                <Box>
                  <Text fontWeight="700" fontSize="sm" mb={1}>Test the classifier</Text>
                  <HStack><Input size="sm" placeholder='e.g. "big pothole on Wulff Road by the school, burst my tyre"' value={testText} onChange={(e) => setTestText(e.target.value)} />
                    <Button size="sm" isDisabled={!testText.trim()} onClick={() => phoneHelpdeskApi.classify(testText).then(setTestResult).catch((e) => setTestResult({ error: e.message }))}>Classify</Button></HStack>
                  {testResult && (testResult.error ? <Alert status="error" mt={2}><AlertIcon />{testResult.error}</Alert> : (
                    <Text fontSize="xs" mt={2}><b>{testResult.label}</b> · {testResult.severity} · {Math.round(testResult.confidence * 100)} % ({testResult.engine}) · genuine {Math.round(testResult.validity.score * 100)} % — engines: rules {testResult.engines.rules}, ML {testResult.engines.ml_engine || '—'}, NB {testResult.engines.naive_bayes || '—'}, LLM {testResult.engines.ollama || '—'}</Text>
                  ))}
                </Box>
                <Box>
                  <Text fontWeight="700" fontSize="sm" mb={1}>Ask the helpdesk AI (streams word by word)</Text>
                  <HStack><Input size="sm" placeholder="e.g. How should I reply to a caller whose car was damaged by a pothole?" value={ask} onChange={(e) => setAsk(e.target.value)} />
                    <Button size="sm" colorScheme="blue" isLoading={streaming} isDisabled={!ask.trim()} onClick={runAsk}>Ask</Button></HStack>
                  {answer && <Box mt={2} p={3} bg={bubbleBg} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap">{answer}{streaming ? ' ▍' : ''}</Box>}
                </Box>
              </VStack>
            )}
          </TabPanel>

          {/* manual intake */}
          <TabPanel px={0}>
            <Text fontSize="sm" color="gray.500" mb={3}>For calls answered by a person: the same AI triage, geotagging, clustering and routing run as for the phone assistant.</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <Textarea placeholder="What the caller said happened" value={intake.description} onChange={(e) => setIntake({ ...intake, description: e.target.value })} rows={4} />
              <VStack align="stretch">
                <Input placeholder="Location (street + cross street / landmark)" value={intake.location_text} onChange={(e) => setIntake({ ...intake, location_text: e.target.value })} />
                <HStack><Input placeholder="Caller phone" value={intake.caller_phone} onChange={(e) => setIntake({ ...intake, caller_phone: e.target.value })} /><Input placeholder="Caller name" value={intake.caller_name} onChange={(e) => setIntake({ ...intake, caller_name: e.target.value })} /></HStack>
                <Select placeholder="Category (let the AI decide)" value={intake.category} onChange={(e) => setIntake({ ...intake, category: e.target.value })}>
                  {Object.entries(CATEGORY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </Select>
              </VStack>
            </SimpleGrid>
            <Button mt={3} colorScheme="green" isLoading={submitting} isDisabled={intake.description.trim().length < 5} onClick={submitIntake}>Log report</Button>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Drawer isOpen={drawer.isOpen} onClose={drawer.onClose} size="xl" placement="right">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Report trace</DrawerHeader>
          <DrawerBody py={4}>
            {traceId && <ReportTracePanel reportId={traceId} onChanged={() => { loadReports(); loadStats(); loadClusters(); setMapKey((k) => k + 1); }} />}
            {traceId && onOpenTicket && <Button mt={4} size="sm" variant="outline" onClick={async () => { try { const t = await phoneHelpdeskApi.trace(traceId); drawer.onClose(); onOpenTicket(t.ticket.id); } catch (_) { /* ignore */ } }}>Open full ticket</Button>}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
// --- END UPDATE
